import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { useAssetContext } from '@/context/AssetContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  BookOpen, 
  Headphones, 
  PenTool, 
  Mic, 
  Clock, 
  ChevronRight,
  ChevronLeft,
  GraduationCap,
  Target,
  TrendingUp,
  Award,
  BarChart3
} from 'lucide-react';

const API_URL = window.location.origin;

const EnglishTestPage = () => {
  const { isDark } = useTheme();
  const { getLogo } = useAssetContext();
  const navigate = useNavigate();
  const [testTypes, setTestTypes] = useState([]);
  const [selectedTest, setSelectedTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    fetchTestTypes();
    fetchHistory();
  }, []);

  const fetchTestTypes = async () => {
    try {
      const res = await fetch(`${API_URL}/api/english-test/types`);
      const data = await res.json();
      if (data.success) {
        setTestTypes(data.test_types);
      }
    } catch (error) {
      console.error('Error fetching test types:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await fetch(`${API_URL}/api/english-test/history/demo_user?limit=5`);
      const data = await res.json();
      if (data.success) {
        setHistory(data.history);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const getSectionIcon = (sectionId) => {
    const icons = {
      reading: BookOpen,
      listening: Headphones,
      writing: PenTool,
      speaking: Mic,
      speaking_writing: PenTool
    };
    return icons[sectionId] || BookOpen;
  };

  const getSectionColor = (sectionId) => {
    const colors = {
      reading: 'text-blue-500',
      listening: 'text-purple-500',
      writing: 'text-green-500',
      speaking: 'text-orange-500',
      speaking_writing: 'text-pink-500'
    };
    return colors[sectionId] || 'text-gray-500';
  };

  const startTest = (testType, section) => {
    navigate(`/uac-desk/english-test/${testType}/${section}`);
  };

  if (loading) {
    return (
      <div className={`min-h-screen pt-20 flex items-center justify-center ${isDark ? 'bg-[#1a2830]' : 'bg-gray-50'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen w-full max-w-[100vw] overflow-x-hidden pt-20 pb-12 ${isDark ? 'bg-[#1a2830]' : 'bg-gray-50'}`} data-testid="english-test-page">
      <div className="w-full max-w-6xl mx-auto px-3 sm:px-6 lg:px-8">
        {/* Back Button */}
        <div className="mb-6">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/uac-desk/dashboard')}
            data-testid="back-to-dashboard-btn"
            className={isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Back to Dashboard
          </Button>
        </div>
        
        {/* Header */}
        <div className="text-center mb-12">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img 
              src={getLogo()} 
              alt="UniXperts" 
              className={`h-12 md:h-14 object-contain ${isDark ? 'brightness-0 invert' : ''}`}
            />
          </div>
          <Badge className={`mb-4 ${isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>
            Quick Practice
          </Badge>
          <h1 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            IELTS & PTE Practice Tests
          </h1>
          <p className={`text-lg max-w-2xl mx-auto ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Assess your English proficiency with AI-powered evaluation. Get instant feedback and track your progress.
          </p>
          {/* View Results Dashboard Button */}
          <Button 
            variant="outline" 
            className={`mt-4 ${isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}`}
            onClick={() => navigate('/uac-desk/english-test/results')}
            data-testid="view-results-btn"
          >
            <BarChart3 className="w-4 h-4 mr-2" /> View All Results
          </Button>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <Card className={isDark ? 'bg-[#233842] border-white/10' : ''}>
            <CardContent className="p-4 text-center">
              <GraduationCap className={`w-8 h-8 mx-auto mb-2 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
              <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {history.length}
              </div>
              <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Tests Taken</div>
            </CardContent>
          </Card>
          <Card className={isDark ? 'bg-[#233842] border-white/10' : ''}>
            <CardContent className="p-4 text-center">
              <Target className={`w-8 h-8 mx-auto mb-2 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
              <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {history.length > 0 ? (history[0].band_score || history[0].points || '-') : '-'}
              </div>
              <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Latest Score</div>
            </CardContent>
          </Card>
          <Card className={isDark ? 'bg-[#233842] border-white/10' : ''}>
            <CardContent className="p-4 text-center">
              <TrendingUp className={`w-8 h-8 mx-auto mb-2 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
              <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {history.length > 0 ? `${Math.round(history.reduce((sum, h) => sum + h.percentage, 0) / history.length)}%` : '-'}
              </div>
              <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Avg. Accuracy</div>
            </CardContent>
          </Card>
          <Card className={isDark ? 'bg-[#233842] border-white/10' : ''}>
            <CardContent className="p-4 text-center">
              <Award className={`w-8 h-8 mx-auto mb-2 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
              <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {history.filter(h => (h.band_score >= 7 || h.points >= 65)).length}
              </div>
              <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>High Scores</div>
            </CardContent>
          </Card>
        </div>

        {/* Test Type Selection */}
        {!selectedTest ? (
          <div className="grid md:grid-cols-2 gap-6 mb-10">
            {testTypes.map((test) => (
              <Card 
                key={test.type}
                className={`cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02] ${
                  isDark ? 'bg-[#233842] border-white/10 hover:border-white/20' : 'hover:border-blue-300'
                }`}
                onClick={() => setSelectedTest(test)}
                data-testid={`test-type-${test.type}`}
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className={isDark ? 'text-white' : ''}>
                      {test.name}
                    </CardTitle>
                    <Badge variant="outline" className={isDark ? 'border-blue-400 text-blue-400' : ''}>
                      {test.scoring}
                    </Badge>
                  </div>
                  <CardDescription className={isDark ? 'text-gray-400' : ''}>
                    {test.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {test.sections.map((section) => {
                      const Icon = getSectionIcon(section.id);
                      return (
                        <div 
                          key={section.id}
                          className={`flex items-center justify-between p-3 rounded-lg ${
                            isDark ? 'bg-[#1a2830]' : 'bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <Icon className={`w-5 h-5 ${getSectionColor(section.id)}`} />
                            <span className={isDark ? 'text-white' : 'text-gray-900'}>{section.name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                            <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                              {section.duration} min
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <Button 
                    className="w-full mt-4"
                    variant={isDark ? 'outline' : 'default'}
                  >
                    Select {test.name} <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          /* Section Selection */
          <div>
            <Button 
              variant="ghost" 
              onClick={() => setSelectedTest(null)}
              className={`mb-6 ${isDark ? 'text-gray-400 hover:text-white' : ''}`}
            >
              ← Back to test selection
            </Button>
            
            <Card className={isDark ? 'bg-[#233842] border-white/10' : ''}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className={isDark ? 'text-white' : ''}>
                      {selectedTest.name} - Quick Practice
                    </CardTitle>
                    <CardDescription className={isDark ? 'text-gray-400' : ''}>
                      Select a section to practice
                    </CardDescription>
                  </div>
                  <Badge variant="outline" className={isDark ? 'border-blue-400 text-blue-400' : ''}>
                    {selectedTest.scoring}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-4">
                  {selectedTest.sections.map((section) => {
                    const Icon = getSectionIcon(section.id);
                    return (
                      <Card 
                        key={section.id}
                        className={`cursor-pointer transition-all hover:shadow-md ${
                          isDark ? 'bg-[#1a2830] border-white/5 hover:border-white/20' : 'hover:border-blue-300'
                        }`}
                        onClick={() => startTest(selectedTest.type, section.id)}
                        data-testid={`section-${section.id}`}
                      >
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4">
                            <div className={`p-3 rounded-xl ${
                              isDark ? 'bg-[#233842]' : 'bg-gray-100'
                            }`}>
                              <Icon className={`w-6 h-6 ${getSectionColor(section.id)}`} />
                            </div>
                            <div className="flex-1">
                              <h3 className={`font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                {section.name}
                              </h3>
                              <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                <div className="flex items-center gap-4">
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-4 h-4" />
                                    {section.duration} min
                                  </span>
                                  <span>
                                    {section.questions || section.tasks}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <ChevronRight className={`w-5 h-5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Recent History */}
        {history.length > 0 && (
          <div className="mt-10">
            <h2 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Recent Practice Sessions
            </h2>
            <div className="space-y-3">
              {history.slice(0, 5).map((item, index) => (
                <Card 
                  key={index}
                  className={isDark ? 'bg-[#233842] border-white/10' : ''}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${isDark ? 'bg-[#1a2830]' : 'bg-gray-100'}`}>
                          {React.createElement(getSectionIcon(item.section), {
                            className: `w-5 h-5 ${getSectionColor(item.section)}`
                          })}
                        </div>
                        <div>
                          <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {item.test_type.toUpperCase()} - {item.section.charAt(0).toUpperCase() + item.section.slice(1).replace('_', ' & ')}
                          </div>
                          <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            {new Date(item.completed_at).toLocaleDateString()} • {item.correct_count}/{item.question_count} correct
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-xl font-bold ${
                          (item.band_score >= 7 || item.points >= 65) 
                            ? 'text-green-500' 
                            : (item.band_score >= 6 || item.points >= 50) 
                            ? 'text-amber-500' 
                            : 'text-red-500'
                        }`}>
                          {item.band_score ? `Band ${item.band_score}` : `${item.points} pts`}
                        </div>
                        <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          {Math.round(item.percentage)}% accuracy
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Disclaimer */}
        <div className={`mt-10 p-4 rounded-lg text-center text-sm ${
          isDark ? 'bg-amber-500/10 text-amber-400' : 'bg-amber-50 text-amber-700'
        }`}>
          <strong>Disclaimer:</strong> These are indicative practice tests. Actual IELTS/PTE scores may vary. 
          This tool is designed to help you practice and identify areas for improvement.
        </div>
      </div>
    </div>
  );
};

export default EnglishTestPage;
